<template>
  <div :style="{ marginLeft: `${item.depth * 32}px` }" class="mb-2">
    <v-card outlined class="staff-card" @click="handleCardClick(item)">
      <div class="staff-card-inner d-flex align-center justify-space-between">

        <!-- Left: expand icon + avatar + info -->
        <div class="d-flex align-center" style="min-width:0;flex:1;">
          <!-- Expand/Collapse -->
          <div class="expand-slot" @click.stop="toggleExpand(item)">
            <v-icon
              v-if="item.hasChildren"
              :style="{
                transform: item.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s',
                color: '#94a3b8',
                cursor: 'pointer',
              }"
              size="20"
            >mdi-chevron-right</v-icon>
            <span v-else style="width:20px;display:inline-block;"></span>
          </div>

          <!-- Avatar -->
          <v-avatar size="44" color="#1261a0" class="mr-3 flex-shrink-0">
            <v-img v-if="item.data.pic" :src="item.data.pic" contain />
            <v-icon v-else size="22" color="white">mdi-account</v-icon>
          </v-avatar>

          <!-- Staff Info -->
          <div style="min-width:0;">
            <div class="staff-name">
              {{ item.data.staffname }}
              <span class="staff-code">({{ item.data.staffcode }})</span>
            </div>
            <div class="staff-pos">{{ item.data.posdesc }}</div>
            <div class="staff-org">
              {{ item.data.companyname }} / {{ item.data.branchname }} /
              {{ item.data.departmentname }} / {{ item.data.sectionname }} /
              {{ item.data.unitname }} / {{ item.data.gradedesc }}
            </div>
          </div>
        </div>

        <!-- Right: count badge + status badge -->
        <div class="d-flex align-center flex-shrink-0 ml-3" style="gap:10px;">
          <span v-if="item.hasChildren" class="count-badge">
            {{ item.childCount }} {{ item.childCount === 1 ? "Staff" : "Staff" }}
          </span>
          <span class="status-badge" :class="statusClass(item.data.statusdesc)">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
              <circle cx="12" cy="12" r="10"/>
              <polyline v-if="item.data.statusdesc === 'OPEN'" points="12 8 12 12 14 14"/>
              <polyline v-else points="20 6 9 17 4 12"/>
            </svg>
            {{ item.data.statusdesc || '—' }}
          </span>
        </div>

      </div>
    </v-card>

    <!-- Recursive children -->
    <transition name="slide-fade">
      <div v-if="item.expanded && item.members && item.members.length > 0">
        <StaffNode
          v-for="child in item.members"
          :key="child.id"
          :item="child"
          :search-query="searchQuery"
          @view-details-node="$emit('view-details-node', $event)"
        />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "StaffNode",
  props: {
    item: { type: Object, required: true },
    searchQuery: { type: String, default: "" },
  },

  methods: {
    toggleExpand(item) {
      if (item.hasChildren) item.expanded = !item.expanded;
    },

    handleCardClick(item) {
      this.$emit("view-details-node", {
        id: item.id,
        staffname: item.data.staffname,
        staffcode: item.data.staffcode,
        statusdesc: item.data.statusdesc,
        posdesc: item.data.posdesc,
        pic: item.data.pic,
      });
    },

    statusClass(status) {
      if (!status) return 'status-default';
      const s = status.toUpperCase();
      if (s === 'OPEN') return 'status-open';
      if (s === 'COMPLETED') return 'status-completed';
      if (s === 'LOCKED') return 'status-locked';
      if (s === 'REVIEWED') return 'status-completed';
      return 'status-default';
    },
  },
};
</script>

<style scoped>
.staff-card {
  border-radius: 10px !important;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
  color: #1a3a5c;
}

.staff-card:hover {
  transform: translateX(3px);
  box-shadow: 0 3px 10px rgba(18, 97, 160, 0.12) !important;
}

.staff-card-inner {
  padding: 10px 14px;
}

.expand-slot {
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.staff-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1a3a5c;
  letter-spacing: normal;
  line-height: 1.3;
}

.staff-code {
  font-weight: 400;
  color: #546e7a;
  font-size: 0.82rem;
}

.staff-pos {
  font-size: 0.78rem;
  font-weight: 600;
  color: #546e7a;
  letter-spacing: normal;
  margin-top: 2px;
}

.staff-org {
  font-size: 0.72rem;
  color: #90a4ae;
  letter-spacing: normal;
  margin-top: 2px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 600px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: #1261a0;
  background: #EEF2FF;
  border: 1px solid #C7D7FF;
  border-radius: 20px;
  padding: 2px 9px;
  letter-spacing: normal;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

.status-open {
  background: #DCFCE7;
  color: #15803D;
  border: 1px solid #BBF7D0;
}

.status-completed {
  background: #DBEAFE;
  color: #1D4ED8;
  border: 1px solid #BFDBFE;
}

.status-locked {
  background: #FEE2E2;
  color: #B91C1C;
  border: 1px solid #FECACA;
}

.status-default {
  background: #F1F5F9;
  color: #475569;
  border: 1px solid #E2E8F0;
}

.slide-fade-enter-active { transition: all 0.25s ease; }
.slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter, .slide-fade-leave-to { transform: translateY(-8px); opacity: 0; }
</style>
